import {
  ON_SALE_MAX_TIME,
  TICKETS_LEFT_MIN,
  TICKETS_LEFT_TIERS,
  TICKETS_LEFT_TIER_0,
  TICKETS_LEFT_TIER_TOP,
  TIME_TO_EVENT_TIMEFRAME,
} from './constants';
import {
  addDays,
  addWeeks,
  diffInDays,
  getDateFromISO,
  getTodayInTimezone,
  isSameDay,
  isSameWeek,
  isWeekend,
} from './dateUtils';

/**
 * Returns the tier value. It is the first one from the following list that is equal or higher than the actual amount: 0, 10, 20, 50, 100, 200, 201
 * @param {number} numTickets number of tickets left
 * @returns the corresponding tier value
 */
const calculateTicketsLeftTier = (numTickets) => {
  if (numTickets === null || numTickets === undefined || numTickets < 0) {
    return null;
  } else if (numTickets === 0) {
    return TICKETS_LEFT_TIER_0;
  }

  return TICKETS_LEFT_TIERS.find((tier) => numTickets <= tier) || TICKETS_LEFT_TIER_TOP;
};

/**
 * Returns true if the number of tickets left is known and it's greater or equal to 0
 * @param {number} numTickets number of tickets left
 * @returns if the tickets left block should be shown or not
 */
const showTicketsLeft = (numTickets) =>
  numTickets !== null && numTickets !== undefined && numTickets >= TICKETS_LEFT_MIN;

/**
 * Returns the timeframe related to the date in the same timezone
 * @param {string} dateLocal local date
 * @returns the associated timeframe to the local date
 */
const calculateEventTimeframe = (dateLocal) => {
  const eventDate = getDateFromISO(dateLocal);
  const todayLocal = getTodayInTimezone(eventDate.zone);

  if (isSameDay(todayLocal, eventDate)) {
    return TIME_TO_EVENT_TIMEFRAME.TODAY;
  } else if (isSameDay(addDays(todayLocal, 1), eventDate)) {
    return TIME_TO_EVENT_TIMEFRAME.TOMORROW;
  } else if (isSameWeek(todayLocal, eventDate)) {
    if (isWeekend(eventDate)) {
      return TIME_TO_EVENT_TIMEFRAME.THIS_WEEKEND;
    }

    return TIME_TO_EVENT_TIMEFRAME.THIS_WEEK;
  } else if (isSameWeek(addWeeks(todayLocal, 1), eventDate)) {
    return TIME_TO_EVENT_TIMEFRAME.NEXT_WEEK;
  } else if (isSameWeek(addWeeks(todayLocal, 2), eventDate)) {
    return TIME_TO_EVENT_TIMEFRAME.TWO_WEEKS;
  }
};

/**
 * It returns the number of days that have passed from the on sale date, rounded down to the neares integer
 * @param {string} onSaleDate the on sale date
 * @returns the rounded down integer of the number of days passed since the on sale date
 */
const calculateOnSaleTimeframe = (onSaleDate) => {
  const onSale = getDateFromISO(onSaleDate);
  if (onSale) {
    const todayLocal = getTodayInTimezone(onSale.zone);

    return Math.floor(diffInDays(todayLocal, onSale));
  }

  return null;
};

/**
 * Returns true if there's a timeframe, it has been in the past (positive) and it has been 14 days ago or fewer
 * @param {number} onSaleTimeFrame difference in days between today and onSale date
 * @returns true if the criteria is true
 */
const showOnSale = (onSaleTimeFrame) => !!onSaleTimeFrame && onSaleTimeFrame > 0 && onSaleTimeFrame <= ON_SALE_MAX_TIME;

export {calculateEventTimeframe, showTicketsLeft, calculateTicketsLeftTier, calculateOnSaleTimeframe, showOnSale};
